import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../contexts/AuthContext';
import { usePermissions } from '../contexts/PermissionsContext';
import { UserBadge } from '../components/UserBadge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import {
  Plus, Loader2, ChevronLeft, ChevronRight, Calendar as CalendarIcon,
  TrendingUp, ShoppingCart, ChevronDown, X, CheckSquare, ExternalLink,
  MapPin, Clock, FileText, Euro, Tag,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  format, startOfMonth, endOfMonth, eachDayOfInterval,
  isSameMonth, isSameDay, addMonths, subMonths, parseISO, isToday,
} from 'date-fns';
import { it } from 'date-fns/locale';

// ── Colours & configs ──────────────────────────────────────────────────────────
const EVENT_TYPES = [
  { value: 'appointment', label: 'Appuntamento', color: '#6366f1' },
  { value: 'visit',       label: 'Visita',        color: '#22c55e' },
  { value: 'call',        label: 'Chiamata',       color: '#3b82f6' },
  { value: 'followup',    label: 'Follow-up',      color: '#f59e0b' },
  { value: 'demo',        label: 'Demo',           color: '#a855f7' },
  { value: 'meeting',     label: 'Riunione',       color: '#f97316' },
];

const STAGES = {
  new_lead:    { label: 'Nuovo Lead',       color: '#6366f1' },
  qualified:   { label: 'Qualificato',      color: '#3b82f6' },
  proposal:    { label: 'Proposta Inviata', color: '#f59e0b' },
  negotiation: { label: 'Trattativa',       color: '#f97316' },
  won:         { label: 'Vinto',            color: '#22c55e' },
  lost:        { label: 'Perso',            color: '#ef4444' },
};

const OPP_COLOR      = '#d97706'; // amber-600
const ORDER_COLOR    = '#0ea5e9'; // sky-500
const ACT_COLORS     = { low: '#3b82f6', medium: '#06b6d4', high: '#f97316', urgent: '#ef4444' };
const ACT_DEFAULT    = '#06b6d4'; // cyan-500

const ACTIVITY_TYPES = [
  { value: 'todo',         label: 'Task' },
  { value: 'call',         label: 'Chiamata' },
  { value: 'email',        label: 'Email' },
  { value: 'followup',     label: 'Follow-up' },
  { value: 'meeting_prep', label: 'Prep. Riunione' },
];

const VAT_RATES = [0, 4, 10, 22];

const emptyItem = () => ({ description: '', quantity: 1, unit_price: 0, discount: 0, vat_rate: 22 });

const formatEuro = (v) =>
  `€ ${Number(v || 0).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// ── Chip component for calendar cells ─────────────────────────────────────────
function CalChip({ item, onClick }) {
  let bg, label;
  if (item._calType === 'event') {
    const cfg = EVENT_TYPES.find(t => t.value === item.type) || EVENT_TYPES[0];
    bg = cfg.color;
    label = item.title;
  } else if (item._calType === 'opportunity') {
    bg = OPP_COLOR;
    label = `◆ ${item.title}`;
  } else if (item._calType === 'activity') {
    bg = ACT_COLORS[item.priority] || ACT_DEFAULT;
    label = `✓ ${item.title}`;
  } else {
    bg = ORDER_COLOR;
    label = `◻ ${item.order_number || item.company_name || 'Ordine'}`;
  }
  return (
    <div
      className="text-xs px-1 py-0.5 rounded truncate text-white cursor-pointer hover:opacity-80 transition-opacity"
      style={{ background: bg }}
      title={`${label} — clicca per aprire`}
      onClick={e => { e.stopPropagation(); onClick && onClick(item); }}
    >
      {label}
    </div>
  );
}

// ── Item Detail Dialog ─────────────────────────────────────────────────────────
function ItemDetailDialog({ item, onClose, navigate }) {
  if (!item) return null;

  const formatEuroLocal = (v) =>
    `€ ${Number(v || 0).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  let title, color, badge, rows, primaryAction, secondaryLabel;

  if (item._calType === 'event') {
    const cfg = EVENT_TYPES.find(t => t.value === item.type) || EVENT_TYPES[0];
    color = cfg.color;
    badge = cfg.label;
    title = item.title;
    rows = [
      item.start_at && { icon: Clock, label: 'Inizio', value: (() => { try { return format(parseISO(item.start_at), "d MMMM yyyy 'alle' HH:mm", { locale: it }); } catch { return item.start_at; } })() },
      item.end_at   && { icon: Clock, label: 'Fine',   value: (() => { try { return format(parseISO(item.end_at),   "d MMMM yyyy 'alle' HH:mm", { locale: it }); } catch { return item.end_at; } })() },
      item.location && { icon: MapPin, label: 'Luogo', value: item.location },
      item.status   && { icon: Tag,    label: 'Stato',  value: { planned:'Pianificato', confirmed:'Confermato', completed:'Completato', cancelled:'Annullato' }[item.status] || item.status },
      item.notes         && { icon: FileText, label: 'Note',           value: item.notes },
      item.assigned_to   && { icon: Tag,      label: 'Commerciale',   value: <UserBadge userId={item.assigned_to} size="xs" /> },
    ].filter(Boolean);
    primaryAction = null; // events are calendar-native, no dedicated page
  } else if (item._calType === 'opportunity') {
    color = OPP_COLOR;
    badge = `Opportunità · ${STAGES[item.stage]?.label || item.stage}`;
    title = item.title;
    rows = [
      item.value         && { icon: Euro,    label: 'Valore',         value: formatEuroLocal(item.value) },
      item.expected_close && { icon: Clock,  label: 'Data chiusura',  value: (() => { try { return format(parseISO(item.expected_close), 'd MMMM yyyy', { locale: it }); } catch { return item.expected_close; } })() },
      item.company_name  && { icon: Tag,     label: 'Cliente',        value: item.company_name },
      item.notes         && { icon: FileText,label: 'Note',           value: item.notes },
    ].filter(Boolean);
    primaryAction = () => { onClose(); navigate('/pipeline'); };
    secondaryLabel = 'Apri Pipeline';
  } else if (item._calType === 'activity') {
    const pMap = { low: 'Bassa', medium: 'Media', high: 'Alta', urgent: 'Urgente' };
    color = ACT_COLORS[item.priority] || ACT_DEFAULT;
    badge = `Attività · ${pMap[item.priority] || item.priority}`;
    title = item.title;
    rows = [
      item.due_date    && { icon: Clock,    label: 'Scadenza',  value: (() => { try { return format(parseISO(item.due_date), 'd MMMM yyyy', { locale: it }); } catch { return item.due_date; } })() },
      item.type        && { icon: Tag,      label: 'Tipo',      value: ACTIVITY_TYPES.find(t => t.value === item.type)?.label || item.type },
      item.status      && { icon: Tag,      label: 'Stato',     value: { todo: 'Da fare', in_progress: 'In corso', done: 'Fatto', cancelled: 'Annullato' }[item.status] || item.status },
      item.notes         && { icon: FileText,label: 'Note',           value: item.notes },
      item.assigned_to   && { icon: Tag,     label: 'Commerciale',   value: <UserBadge userId={item.assigned_to} size="xs" /> },
    ].filter(Boolean);
    primaryAction = () => { onClose(); navigate('/activities'); };
    secondaryLabel = 'Vai ad Attività';
  } else {
    // order
    const STATUS_LABELS = { draft: 'Bozza', confirmed: 'Confermato', processing: 'In lavorazione', shipped: 'Spedito', delivered: 'Consegnato', cancelled: 'Annullato' };
    color = ORDER_COLOR;
    badge = `Ordine · ${STATUS_LABELS[item.status] || item.status}`;
    title = item.order_number || 'Ordine';
    rows = [
      item.company_name && { icon: Tag,     label: 'Cliente', value: item.company_name },
      item.order_date   && { icon: Clock,   label: 'Data ordine', value: (() => { try { return format(parseISO(item.order_date), 'd MMMM yyyy', { locale: it }); } catch { return item.order_date; } })() },
      item.expected_delivery && { icon: Clock, label: 'Consegna prevista', value: (() => { try { return format(parseISO(item.expected_delivery), 'd MMMM yyyy', { locale: it }); } catch { return item.expected_delivery; } })() },
      item.total        && { icon: Euro,    label: 'Totale', value: formatEuroLocal(item.total) },
      item.notes        && { icon: FileText,label: 'Note',   value: item.notes },
      item.assigned_to  && { icon: Tag,     label: 'Commerciale', value: <UserBadge userId={item.assigned_to} size="xs" /> },
    ].filter(Boolean);
    const orderId = item._id || item.id;
    primaryAction = () => { onClose(); navigate(`/orders/${orderId}`); };
    secondaryLabel = 'Apri Ordine';
  }

  return (
    <Dialog open={!!item} onOpenChange={onClose}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ background: color }} />
            <DialogTitle className="text-base" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {title}
            </DialogTitle>
          </div>
          <Badge variant="outline" className="w-fit text-xs mt-0.5">{badge}</Badge>
        </DialogHeader>

        {rows.length > 0 && (
          <div className="space-y-2 mt-1">
            <Separator />
            {rows.map(({ icon: Icon, label, value }, i) => (
              <div key={i} className="flex items-start gap-2.5 text-sm">
                <Icon size={14} className="text-muted-foreground flex-shrink-0 mt-0.5" />
                <div className="min-w-0">
                  <span className="text-muted-foreground text-xs block">{label}</span>
                  <span className="break-words">{value}</span>
                </div>
              </div>
            ))}
          </div>
        )}

        {primaryAction && (
          <div className="flex gap-2 pt-1 mt-2">
            <Button variant="outline" size="sm" className="flex-1" onClick={onClose}>Chiudi</Button>
            <Button size="sm" className="flex-1" onClick={primaryAction} data-testid="item-detail-open-btn">
              <ExternalLink size={13} className="mr-1.5" />{secondaryLabel}
            </Button>
          </div>
        )}
        {!primaryAction && (
          <Button variant="outline" size="sm" className="w-full mt-2" onClick={onClose}>Chiudi</Button>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────
export default function CalendarPage() {
  const navigate = useNavigate();
  const { canCreate } = usePermissions();
  const [currentDate, setCurrentDate]   = useState(new Date());
  const [events, setEvents]             = useState([]);
  const [activities, setActivities]     = useState([]);
  const [opportunities, setOpportunities] = useState([]);
  const [orders, setOrders]             = useState([]);
  const [companies, setCompanies]       = useState([]);
  const [contacts, setContacts]         = useState([]);
  const [loading, setLoading]           = useState(true);
  const [view, setView]                 = useState('month');
  const [selectedItem, setSelectedItem] = useState(null);

  // filter: 'all' | 'events' | 'opportunities' | 'orders' | 'activities'
  const [filter, setFilter]             = useState('all');

  // Which dialog is open: null | 'event' | 'opportunity' | 'order' | 'activity' | 'dayPicker'
  const [activeDialog, setActiveDialog] = useState(null);
  const [pendingDay, setPendingDay]     = useState(null);
  const [saving, setSaving]             = useState(false);

  // Dropdown "Aggiungi" toggle
  const [addMenuOpen, setAddMenuOpen]   = useState(false);
  const addMenuRef                      = useRef(null);

  // ── Forms ────────────────────────────────────────────────────────────────────
  const [eventForm, setEventForm] = useState({
    title: '', type: 'appointment', start_at: '', end_at: '',
    all_day: false, location: '', notes: '', status: 'planned',
  });
  const [oppForm, setOppForm] = useState({
    title: '', company_id: '', value: '', stage: 'new_lead', expected_close: '', notes: '',
  });
  const [orderForm, setOrderForm]   = useState({ company_id: '', expected_delivery: '', notes: '' });
  const [orderItems, setOrderItems] = useState([emptyItem()]);
  const [actForm, setActForm]       = useState({ title: '', type: 'todo', priority: 'medium', due_date: '', description: '', assigned_to: '' });
  const [users, setUsers]           = useState([]);
  const [userFilter, setUserFilter] = useState('');

  // ── Close add-menu on outside click ─────────────────────────────────────────
  useEffect(() => {
    const handler = (e) => {
      if (addMenuRef.current && !addMenuRef.current.contains(e.target)) setAddMenuOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // ── Data fetching ─────────────────────────────────────────────────────────────
  const fetchAll = async () => {
    const start = format(startOfMonth(currentDate), 'yyyy-MM-dd');
    const end   = format(endOfMonth(currentDate), 'yyyy-MM-dd');
    try {
      const [evRes, oppRes, ordRes, compRes, actRes] = await Promise.all([
        axios.get(`${API}/calendar`, { params: { start, end } }),
        axios.get(`${API}/opportunities`, { params: { limit: 500 } }),
        axios.get(`${API}/orders`, { params: { limit: 500 } }),
        axios.get(`${API}/companies`).catch(() => ({ data: [] })),
        axios.get(`${API}/activities`, { params: { limit: 500 } }),
      ]);
      setEvents(evRes.data);
      setOpportunities(oppRes.data);
      setOrders(ordRes.data);
      setCompanies(compRes.data);
      setActivities(actRes.data);
    } catch { toast.error('Errore caricamento dati'); } finally { setLoading(false); }
  };

  useEffect(() => {
    axios.get(`${API}/contacts`).then(r => setContacts(r.data)).catch(() => {});
    axios.get(`${API}/users?limit=200`).then(r => setUsers(r.data)).catch(() => {});
  }, []);

  useEffect(() => { fetchAll(); }, [currentDate]);

  // ── Calendar items for a day ──────────────────────────────────────────────────
  const matchesUserFilter = (item) => !userFilter || item.assigned_to === userFilter;

  const itemsForDay = (day) => {
    const result = [];
    if (filter === 'all' || filter === 'events') {
      events.forEach(e => {
        try { if (isSameDay(parseISO(e.start_at), day) && matchesUserFilter(e)) result.push({ ...e, _calType: 'event' }); } catch {}
      });
    }
    if (filter === 'all' || filter === 'opportunities') {
      opportunities.forEach(o => {
        try { if (o.expected_close && isSameDay(parseISO(o.expected_close), day) && matchesUserFilter(o)) result.push({ ...o, _calType: 'opportunity' }); } catch {}
      });
    }
    if (filter === 'all' || filter === 'orders') {
      orders.forEach(o => {
        try { if (o.order_date && isSameDay(parseISO(o.order_date), day) && matchesUserFilter(o)) result.push({ ...o, _calType: 'order' }); } catch {}
      });
    }
    if (filter === 'all' || filter === 'activities') {
      activities.forEach(a => {
        try { if (a.due_date && isSameDay(parseISO(a.due_date), day) && matchesUserFilter(a)) result.push({ ...a, _calType: 'activity' }); } catch {}
      });
    }
    return result;
  };

  // ── Day click → type chooser ───────────────────────────────────────────────────
  const handleDayClick = (day) => {
    setPendingDay(day);
    setActiveDialog('dayPicker');
  };

  const openFormForType = (type) => {
    const day = pendingDay;
    setActiveDialog(null);
    if (type === 'event') {
      const dateStr = day ? format(day, "yyyy-MM-dd'T'09:00") : '';
      setEventForm(f => ({ ...f, start_at: dateStr, end_at: day ? format(day, "yyyy-MM-dd'T'10:00") : '' }));
      setActiveDialog('event');
    } else if (type === 'opportunity') {
      setOppForm(f => ({ ...f, expected_close: day ? format(day, 'yyyy-MM-dd') : '' }));
      setActiveDialog('opportunity');
    } else if (type === 'activity') {
      setActForm(f => ({ ...f, due_date: day ? format(day, 'yyyy-MM-dd') : '' }));
      setActiveDialog('activity');
    } else {
      setOrderForm(f => ({ ...f, expected_delivery: day ? format(day, 'yyyy-MM-dd') : '' }));
      setOrderItems([emptyItem()]);
      setActiveDialog('order');
    }
  };

  // ── Event submit ──────────────────────────────────────────────────────────────
  const handleEventCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...eventForm };
      if (!payload.end_at) payload.end_at = payload.start_at;
      await axios.post(`${API}/calendar`, payload);
      toast.success('Evento creato');
      setActiveDialog(null);
      setEventForm({ title:'', type:'appointment', start_at:'', end_at:'', all_day:false, location:'', notes:'', status:'planned', assigned_to:'' });
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Errore'); } finally { setSaving(false); }
  };

  // ── Opportunity submit ────────────────────────────────────────────────────────
  const handleOppCreate = async (e) => {
    e.preventDefault();
    if (!oppForm.title.trim()) { toast.error('Inserisci il titolo'); return; }
    setSaving(true);
    try {
      const payload = {
        title: oppForm.title,
        stage: oppForm.stage,
        value: parseFloat(oppForm.value) || 0,
        expected_close: oppForm.expected_close || undefined,
        notes: oppForm.notes || undefined,
      };
      if (oppForm.company_id)  payload.company_id  = oppForm.company_id;
      if (oppForm.assigned_to) payload.assigned_to = oppForm.assigned_to;
      await axios.post(`${API}/opportunities`, payload);
      toast.success('Opportunità creata');
      setActiveDialog(null);
      setOppForm({ title: '', company_id: '', value: '', stage: 'new_lead', expected_close: '', notes: '', assigned_to: '' });
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Errore'); } finally { setSaving(false); }
  };

  // ── Order submit ──────────────────────────────────────────────────────────────
  const handleOrderCreate = async (e) => {
    e.preventDefault();
    if (!orderForm.company_id) { toast.error('Seleziona un cliente'); return; }
    const validItems = orderItems.filter(it => it.description.trim());
    if (!validItems.length) { toast.error('Aggiungi almeno un articolo'); return; }
    setSaving(true);
    try {
      const payload = { company_id: orderForm.company_id, items: validItems };
      if (orderForm.expected_delivery) payload.expected_delivery = orderForm.expected_delivery;
      if (orderForm.notes) payload.notes = orderForm.notes;
      const res = await axios.post(`${API}/orders`, payload);
      toast.success(`Ordine ${res.data.order_number} creato`);
      setActiveDialog(null);
      setOrderForm({ company_id: '', expected_delivery: '', notes: '' });
      setOrderItems([emptyItem()]);
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Errore'); } finally { setSaving(false); }
  };

  // ── Activity submit ───────────────────────────────────────────────────────────
  const handleActivityCreate = async (e) => {
    e.preventDefault();
    if (!actForm.title.trim()) { toast.error('Inserisci il titolo'); return; }
    setSaving(true);
    try {
      const payload = { title: actForm.title, type: actForm.type, priority: actForm.priority, status: 'todo' };
      if (actForm.due_date)    payload.due_date    = actForm.due_date;
      if (actForm.description) payload.description = actForm.description;
      if (actForm.assigned_to) payload.assigned_to = actForm.assigned_to;
      await axios.post(`${API}/activities`, payload);
      toast.success('Attività creata');
      setActiveDialog(null);
      setActForm({ title: '', type: 'todo', priority: 'medium', due_date: '', description: '', assigned_to: '' });
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Errore'); } finally { setSaving(false); }
  };

  // ── Stats (current month only) ────────────────────────────────────────────────
  const oppThisMonth = opportunities.filter(o => {
    try { return o.expected_close && isSameMonth(parseISO(o.expected_close), currentDate); } catch { return false; }
  });
  const ordThisMonth = orders.filter(o => {
    try { return o.order_date && isSameMonth(parseISO(o.order_date), currentDate); } catch { return false; }
  });
  const actThisMonth = activities.filter(a => {
    try { return a.due_date && isSameMonth(parseISO(a.due_date), currentDate); } catch { return false; }
  });
  const visibleEventsCount = filter === 'all' || filter === 'events' ? events.length : 0;
  const visibleOppCount    = (filter === 'all' || filter === 'opportunities') ? oppThisMonth.length : 0;
  const visibleOrdCount    = (filter === 'all' || filter === 'orders') ? ordThisMonth.length : 0;
  const visibleActCount    = (filter === 'all' || filter === 'activities') ? actThisMonth.length : 0;
  const totalVisible       = visibleEventsCount + visibleOppCount + visibleOrdCount + visibleActCount;

  const days            = eachDayOfInterval({ start: startOfMonth(currentDate), end: endOfMonth(currentDate) });
  const startDayOfWeek  = (startOfMonth(currentDate).getDay() + 6) % 7;
  const weekDays        = ['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'];

  // ── Agenda: combine all 4 types sorted by date ────────────────────────────────
  const agendaItems = [
    ...( (filter === 'all' || filter === 'events')        ? events.map(e => ({ ...e, _calType: 'event', _sortDate: e.start_at }))          : [] ),
    ...( (filter === 'all' || filter === 'opportunities') ? opportunities.filter(o => o.expected_close).map(o => ({ ...o, _calType: 'opportunity', _sortDate: o.expected_close })) : [] ),
    ...( (filter === 'all' || filter === 'orders')        ? orders.filter(o => o.order_date).map(o => ({ ...o, _calType: 'order', _sortDate: o.order_date })) : [] ),
    ...( (filter === 'all' || filter === 'activities')    ? activities.filter(a => a.due_date).map(a => ({ ...a, _calType: 'activity', _sortDate: a.due_date })) : [] ),
  ]
    .filter(it => new Date(it._sortDate) >= new Date(new Date().setHours(0,0,0,0)))
    .filter(it => matchesUserFilter(it))
    .sort((a, b) => new Date(a._sortDate) - new Date(b._sortDate))
    .slice(0, 20);

  // ── Filter pill helper ─────────────────────────────────────────────────────────
  const FilterPill = ({ value, label, color, count }) => (
    <button
      onClick={() => setFilter(value)}
      data-testid={`filter-${value}`}
      className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border transition-all ${
        filter === value
          ? 'text-white border-transparent shadow-sm'
          : 'border-border hover:bg-muted text-muted-foreground'
      }`}
      style={filter === value ? { background: color } : {}}
    >
      {label}
      <span className={`text-[10px] font-bold px-1 rounded-full ${filter === value ? 'bg-white/20' : 'bg-muted'}`}>
        {count}
      </span>
    </button>
  );

  return (
    <div className="space-y-5">
      {/* ── Header ──────────────────────────────────────────────────────────── */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>Calendario Commerciale</h1>
          <p className="text-muted-foreground text-sm mt-1">{totalVisible} elementi visibili questo mese</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* View toggle */}
          <div className="flex border border-border rounded-lg overflow-hidden">
            {['month', 'agenda'].map(v => (
              <Button key={v} variant={view === v ? 'default' : 'ghost'} size="sm" className="rounded-none" onClick={() => setView(v)} data-testid={`calendar-${v}-btn`}>
                {v === 'month' ? 'Mese' : 'Agenda'}
              </Button>
            ))}
          </div>
          {/* Add dropdown */}
          {canCreate('calendar') && (
          <div className="relative" ref={addMenuRef}>
            <Button onClick={() => setAddMenuOpen(o => !o)} data-testid="add-calendar-btn">
              <Plus size={16} className="mr-1.5" />Aggiungi
              <ChevronDown size={14} className={`ml-1 transition-transform ${addMenuOpen ? 'rotate-180' : ''}`} />
            </Button>
            {addMenuOpen && (
              <div className="absolute right-0 top-full mt-1 z-50 w-52 rounded-xl border border-border bg-card shadow-lg py-1 animate-fadeIn">
                <button
                  className="flex w-full items-center gap-2.5 px-4 py-2.5 text-sm hover:bg-muted transition-colors"
                  onClick={() => { setAddMenuOpen(false); setPendingDay(null); setEventForm(f => ({...f, start_at:'', end_at:''})); setActiveDialog('event'); }}
                  data-testid="add-event-menu-btn"
                >
                  <div className="w-2.5 h-2.5 rounded-full bg-indigo-500 flex-shrink-0" />
                  <span>Nuovo Evento</span>
                </button>
                <button
                  className="flex w-full items-center gap-2.5 px-4 py-2.5 text-sm hover:bg-muted transition-colors"
                  onClick={() => { setAddMenuOpen(false); setPendingDay(null); setOppForm(f => ({...f, expected_close:''})); setActiveDialog('opportunity'); }}
                  data-testid="add-opp-menu-btn"
                >
                  <TrendingUp size={14} className="flex-shrink-0 text-amber-500" />
                  <span>Nuova Opportunità</span>
                </button>
                <button
                  className="flex w-full items-center gap-2.5 px-4 py-2.5 text-sm hover:bg-muted transition-colors"
                  onClick={() => { setAddMenuOpen(false); setPendingDay(null); setOrderForm(f => ({...f, expected_delivery:''})); setOrderItems([emptyItem()]); setActiveDialog('order'); }}
                  data-testid="add-order-menu-btn"
                >
                  <ShoppingCart size={14} className="flex-shrink-0 text-sky-500" />
                  <span>Nuovo Ordine</span>
                </button>
                <button
                  className="flex w-full items-center gap-2.5 px-4 py-2.5 text-sm hover:bg-muted transition-colors"
                  onClick={() => { setAddMenuOpen(false); setPendingDay(null); setActForm(f => ({...f, due_date:''})); setActiveDialog('activity'); }}
                  data-testid="add-activity-menu-btn"
                >
                  <CheckSquare size={14} className="flex-shrink-0" style={{ color: ACT_DEFAULT }} />
                  <span>Nuova Attività</span>
                </button>
              </div>
            )}
          </div>
          )}
        </div>
      </div>

      {/* ── Filter pills ────────────────────────────────────────────────────── */}
      <div className="flex flex-wrap items-center gap-2" data-testid="calendar-filters">
        <FilterPill value="all"           label="Tutti"          color="#475569" count={totalVisible} />
        <FilterPill value="events"        label="Solo Eventi"    color="#6366f1" count={events.length} />
        <FilterPill value="opportunities" label="Opportunità"    color={OPP_COLOR} count={oppThisMonth.length} />
        <FilterPill value="orders"        label="Ordini"         color={ORDER_COLOR} count={ordThisMonth.length} />
        <FilterPill value="activities"    label="Attività"       color={ACT_DEFAULT} count={actThisMonth.length} />
        {users.length > 0 && (
          <div className="ml-auto">
            <Select value={userFilter || "__all__"} onValueChange={v => setUserFilter(v === "__all__" ? "" : v)}>
              <SelectTrigger className="h-7 text-xs w-44 px-2" data-testid="calendar-user-filter">
                <SelectValue placeholder="Tutti i commerciali" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Tutti i commerciali</SelectItem>
                {users.map(u => (
                  <SelectItem key={u.id || u._id} value={u.id || u._id}>
                    {u.name || u.email}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      {/* ── Month view ──────────────────────────────────────────────────────── */}
      {view === 'month' ? (
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="capitalize text-lg" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {format(currentDate, 'MMMM yyyy', { locale: it })}
              </CardTitle>
              <div className="flex gap-1">
                <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setCurrentDate(d => subMonths(d, 1))} data-testid="prev-month">
                  <ChevronLeft size={16} />
                </Button>
                <Button variant="outline" size="sm" onClick={() => setCurrentDate(new Date())} data-testid="today-btn">Oggi</Button>
                <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setCurrentDate(d => addMonths(d, 1))} data-testid="next-month">
                  <ChevronRight size={16} />
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex justify-center py-8"><Loader2 className="animate-spin text-primary" size={24} /></div>
            ) : (
              <div>
                <div className="grid grid-cols-7 mb-1">
                  {weekDays.map(d => (
                    <div key={d} className="text-center text-xs font-bold text-slate-600 dark:text-slate-400 py-2 border-b border-border">{d}</div>
                  ))}
                </div>
                <div className="grid grid-cols-7 gap-0.5">
                  {Array.from({ length: startDayOfWeek }).map((_, i) => (
                    <div key={`empty-${i}`} className="min-h-[100px] p-1" />
                  ))}
                  {days.map(day => {
                    const dayItems = itemsForDay(day);
                    const today    = isToday(day);
                    return (
                      <div
                        key={day.toISOString()}
                        onClick={() => handleDayClick(day)}
                        className={`min-h-[100px] p-1.5 rounded-lg cursor-pointer hover:bg-accent transition-colors border border-transparent hover:border-border/50 ${
                          today ? 'bg-primary/10 ring-1 ring-primary' : ''
                        } ${!isSameMonth(day, currentDate) ? 'opacity-40' : ''}`}
                        data-testid={`calendar-day-${format(day, 'yyyy-MM-dd')}`}
                      >
                        <span className={`text-xs font-bold block text-right mb-1 ${today ? 'text-primary' : 'text-slate-700 dark:text-slate-300'}`}>
                          {format(day, 'd')}
                        </span>
                        <div className="space-y-0.5">
                          {dayItems.slice(0, 3).map((item, idx) => (
                            <CalChip key={idx} item={item} onClick={setSelectedItem} />
                          ))}
                          {dayItems.length > 3 && (
                            <p className="text-xs text-muted-foreground text-center font-medium">+{dayItems.length - 3}</p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      ) : (
        /* ── Agenda view ──────────────────────────────────────────────────── */
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Prossimi Appuntamenti
            </CardTitle>
          </CardHeader>
          <CardContent>
            {agendaItems.length === 0 ? (
              <div className="text-center py-8">
                <CalendarIcon size={32} className="text-muted-foreground mx-auto mb-2" />
                <p className="text-muted-foreground text-sm">Nessun elemento in programma</p>
              </div>
            ) : (
              <div className="space-y-3">
                {agendaItems.map((item, idx) => {
                  let borderColor, badgeLabel, dateLabel, subtitle;
                  if (item._calType === 'event') {
                    const cfg = EVENT_TYPES.find(t => t.value === item.type) || EVENT_TYPES[0];
                    borderColor = cfg.color;
                    badgeLabel  = cfg.label;
                    try { dateLabel = format(parseISO(item.start_at), "d MMMM yyyy 'alle' HH:mm", { locale: it }); } catch { dateLabel = item.start_at; }
                    subtitle    = item.location;
                  } else if (item._calType === 'opportunity') {
                    borderColor = OPP_COLOR;
                    badgeLabel  = `Opp. · ${STAGES[item.stage]?.label || item.stage}`;
                    try { dateLabel = `Chiusura: ${format(parseISO(item.expected_close), 'd MMMM yyyy', { locale: it })}`; } catch { dateLabel = item.expected_close; }
                    subtitle    = item.value ? formatEuro(item.value) : null;
                  } else if (item._calType === 'activity') {
                    borderColor = ACT_COLORS[item.priority] || ACT_DEFAULT;
                    const pLabel = { low: 'Bassa', medium: 'Media', high: 'Alta', urgent: 'Urgente' }[item.priority] || item.priority;
                    badgeLabel  = `Attività · ${pLabel}`;
                    try { dateLabel = `Scadenza: ${format(parseISO(item.due_date), 'd MMMM yyyy', { locale: it })}`; } catch { dateLabel = item.due_date; }
                    subtitle    = item.description || null;
                  } else {
                    borderColor = ORDER_COLOR;
                    badgeLabel  = `Ordine · ${item.status || 'draft'}`;
                    try { dateLabel = `Data: ${format(parseISO(item.order_date), 'd MMMM yyyy', { locale: it })}`; } catch { dateLabel = item.order_date; }
                    subtitle    = item.total ? formatEuro(item.total) : null;
                  }
                  return (
                    <div
                      key={idx}
                      className="flex items-start gap-3 p-3 rounded-lg border border-border hover:bg-muted/30 transition-colors cursor-pointer"
                      onClick={() => setSelectedItem(item)}
                      data-testid={`agenda-item-${idx}`}
                    >
                      <div className="w-1 min-h-[40px] rounded-full flex-shrink-0" style={{ background: borderColor }} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2 flex-wrap">
                          <p className="font-medium text-sm">{item.title || item.order_number || '—'}</p>
                          <div className="flex items-center gap-1.5">
                            <Badge variant="outline" className="text-xs flex-shrink-0">{badgeLabel}</Badge>
                            <ExternalLink size={12} className="text-muted-foreground flex-shrink-0" />
                          </div>
                        </div>
                        <p className="text-xs text-muted-foreground mt-0.5">{dateLabel}</p>
                        {subtitle && <p className="text-xs text-muted-foreground">{subtitle}</p>}
                        {item.assigned_to && <div className="mt-0.5"><UserBadge userId={item.assigned_to} size="xs" /></div>}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* ── Legend ──────────────────────────────────────────────────────────── */}
      <div className="flex flex-wrap gap-x-5 gap-y-2">
        <p className="text-xs font-medium text-muted-foreground w-full">Legenda:</p>
        {EVENT_TYPES.map(t => (
          <div key={t.value} className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: t.color }} />
            {t.label}
          </div>
        ))}
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <TrendingUp size={11} className="flex-shrink-0" style={{ color: OPP_COLOR }} />
          Opportunità (data chiusura)
        </div>
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <ShoppingCart size={11} className="flex-shrink-0" style={{ color: ORDER_COLOR }} />
          Ordini (data ordine)
        </div>
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <CheckSquare size={11} className="flex-shrink-0" style={{ color: ACT_DEFAULT }} />
          Attività (data scadenza) — colore = priorità
        </div>
      </div>

      {/* ══════════════════════════════════════════════════════════════════════
          DIALOGS
      ══════════════════════════════════════════════════════════════════════ */}

      {/* Day-type picker ─────────────────────────────────────────────────────── */}
      <Dialog open={activeDialog === 'dayPicker'} onOpenChange={() => setActiveDialog(null)}>
        <DialogContent className="max-w-xs">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
              {pendingDay ? format(pendingDay, "d MMMM yyyy", { locale: it }) : 'Aggiungi'}
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground -mt-1 mb-2">Cosa vuoi aggiungere?</p>
          <div className="flex flex-col gap-2">
            <Button variant="outline" className="justify-start gap-2 h-11" onClick={() => openFormForType('event')} data-testid="day-pick-event">
              <div className="w-3 h-3 rounded-full bg-indigo-500" />Nuovo Evento
            </Button>
            <Button variant="outline" className="justify-start gap-2 h-11" onClick={() => openFormForType('opportunity')} data-testid="day-pick-opp">
              <TrendingUp size={14} className="text-amber-500" />Nuova Opportunità
            </Button>
            <Button variant="outline" className="justify-start gap-2 h-11" onClick={() => openFormForType('activity')} data-testid="day-pick-activity">
              <CheckSquare size={14} style={{ color: ACT_DEFAULT }} />Nuova Attività
            </Button>
            <Button variant="outline" className="justify-start gap-2 h-11" onClick={() => openFormForType('order')} data-testid="day-pick-order">
              <ShoppingCart size={14} className="text-sky-500" />Nuovo Ordine
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Event form ──────────────────────────────────────────────────────────── */}
      <Dialog open={activeDialog === 'event'} onOpenChange={() => setActiveDialog(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Nuovo Evento</DialogTitle></DialogHeader>
          <form onSubmit={handleEventCreate} className="space-y-4 mt-2">
            <div className="space-y-1.5">
              <Label>Titolo *</Label>
              <Input value={eventForm.title} onChange={e => setEventForm(f => ({ ...f, title: e.target.value }))} required data-testid="event-title-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Tipo</Label>
              <Select value={eventForm.type} onValueChange={v => setEventForm(f => ({ ...f, type: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{EVENT_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Inizio *</Label>
                <Input type="datetime-local" value={eventForm.start_at} onChange={e => setEventForm(f => ({ ...f, start_at: e.target.value }))} required />
              </div>
              <div className="space-y-1.5">
                <Label>Fine</Label>
                <Input type="datetime-local" value={eventForm.end_at} onChange={e => setEventForm(f => ({ ...f, end_at: e.target.value }))} />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Luogo / Link meeting</Label>
              <Input value={eventForm.location} onChange={e => setEventForm(f => ({ ...f, location: e.target.value }))} placeholder="Via Roma 1 / https://meet.google.com/..." />
            </div>
            <div className="space-y-1.5">
              <Label>Stato</Label>
              <Select value={eventForm.status} onValueChange={v => setEventForm(f => ({ ...f, status: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="planned">Pianificato</SelectItem>
                  <SelectItem value="confirmed">Confermato</SelectItem>
                  <SelectItem value="completed">Completato</SelectItem>
                  <SelectItem value="cancelled">Annullato</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Note</Label>
              <Textarea value={eventForm.notes} onChange={e => setEventForm(f => ({ ...f, notes: e.target.value }))} rows={2} />
            </div>
            {users.length > 0 && (
              <div className="space-y-1.5">
                <Label>Assegna a</Label>
                <Select value={eventForm.assigned_to || "__none__"} onValueChange={v => setEventForm(f => ({ ...f, assigned_to: v === '__none__' ? '' : v }))}>
                  <SelectTrigger data-testid="event-assigned-to"><SelectValue placeholder="Seleziona commerciale..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">— Nessuno</SelectItem>
                    {users.map(u => <SelectItem key={u.id||u._id} value={u.id||u._id}>{u.name||u.email}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="flex gap-2 pt-1">
              <Button type="button" variant="outline" className="flex-1" onClick={() => setActiveDialog(null)}>Annulla</Button>
              <Button type="submit" className="flex-1" disabled={saving} data-testid="save-event-btn">
                {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Salva Evento
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Opportunity quick-add form ──────────────────────────────────────────── */}
      <Dialog open={activeDialog === 'opportunity'} onOpenChange={() => setActiveDialog(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }} className="flex items-center gap-2">
              <TrendingUp size={18} className="text-amber-500" />Nuova Opportunità
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleOppCreate} className="space-y-4 mt-2">
            <div className="space-y-1.5">
              <Label>Titolo *</Label>
              <Input value={oppForm.title} onChange={e => setOppForm(f => ({ ...f, title: e.target.value }))} required placeholder="Es. Fornitura sementi 2026" data-testid="opp-title-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Cliente</Label>
                <Select value={oppForm.company_id} onValueChange={v => setOppForm(f => ({ ...f, company_id: v }))}>
                  <SelectTrigger><SelectValue placeholder="Seleziona..." /></SelectTrigger>
                  <SelectContent>{companies.map(c => <SelectItem key={c._id || c.id} value={c._id || c.id}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Stadio</Label>
                <Select value={oppForm.stage} onValueChange={v => setOppForm(f => ({ ...f, stage: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(STAGES).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Valore (€)</Label>
                <Input type="number" min="0" step="0.01" value={oppForm.value} onChange={e => setOppForm(f => ({ ...f, value: e.target.value }))} placeholder="0.00" data-testid="opp-value-input" />
              </div>
              <div className="space-y-1.5">
                <Label>Data chiusura prevista</Label>
                <Input type="date" value={oppForm.expected_close} onChange={e => setOppForm(f => ({ ...f, expected_close: e.target.value }))} data-testid="opp-close-date-input" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Note</Label>
              <Textarea value={oppForm.notes} onChange={e => setOppForm(f => ({ ...f, notes: e.target.value }))} rows={2} />
            </div>
            {users.length > 0 && (
              <div className="space-y-1.5">
                <Label>Assegna a</Label>
                <Select value={oppForm.assigned_to || '__none__'} onValueChange={v => setOppForm(f => ({ ...f, assigned_to: v === '__none__' ? '' : v }))}>
                  <SelectTrigger data-testid="opp-assigned-to"><SelectValue placeholder="Seleziona commerciale..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">— Nessuno</SelectItem>
                    {users.map(u => <SelectItem key={u.id||u._id} value={u.id||u._id}>{u.name||u.email}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="flex gap-2 pt-1">
              <Button type="button" variant="outline" className="flex-1" onClick={() => setActiveDialog(null)}>Annulla</Button>
              <Button type="submit" className="flex-1 bg-amber-600 hover:bg-amber-700" disabled={saving} data-testid="save-opp-btn">
                {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Crea Opportunità
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Order quick-add form ────────────────────────────────────────────────── */}
      <Dialog open={activeDialog === 'order'} onOpenChange={() => setActiveDialog(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }} className="flex items-center gap-2">
              <ShoppingCart size={18} className="text-sky-500" />Nuovo Ordine
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleOrderCreate} className="space-y-4 mt-2">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Cliente *</Label>
                <Select value={orderForm.company_id} onValueChange={v => setOrderForm(f => ({ ...f, company_id: v }))}>
                  <SelectTrigger><SelectValue placeholder="Seleziona cliente..." /></SelectTrigger>
                  <SelectContent>{companies.map(c => <SelectItem key={c._id || c.id} value={c._id || c.id}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Consegna prevista</Label>
                <Input type="date" value={orderForm.expected_delivery} onChange={e => setOrderForm(f => ({ ...f, expected_delivery: e.target.value }))} />
              </div>
            </div>

            {/* Items table */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-sm font-semibold">Articoli / Servizi</Label>
                <Button type="button" variant="outline" size="sm" onClick={() => setOrderItems(p => [...p, emptyItem()])}>
                  <Plus size={13} className="mr-1" />Aggiungi riga
                </Button>
              </div>
              <div className="border rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="text-left px-3 py-2 font-medium text-muted-foreground">Descrizione</th>
                        <th className="text-right px-1 py-2 font-medium text-muted-foreground w-16">Qtà</th>
                        <th className="text-right px-1 py-2 font-medium text-muted-foreground w-24">€/u</th>
                        <th className="text-right px-1 py-2 font-medium text-muted-foreground w-16">IVA%</th>
                        <th className="w-7" />
                      </tr>
                    </thead>
                    <tbody>
                      {orderItems.map((it, idx) => (
                        <tr key={idx} className="border-t border-border">
                          <td className="px-3 py-1.5">
                            <Input value={it.description} onChange={e => setOrderItems(p => p.map((r, i) => i===idx ? {...r, description: e.target.value} : r))} placeholder="Articolo..." className="h-8 text-sm" data-testid={`cal-item-desc-${idx}`} />
                          </td>
                          <td className="px-1 py-1.5">
                            <Input type="number" min="0" step="0.01" value={it.quantity} onChange={e => setOrderItems(p => p.map((r, i) => i===idx ? {...r, quantity: parseFloat(e.target.value)||0} : r))} className="h-8 text-sm text-right" />
                          </td>
                          <td className="px-1 py-1.5">
                            <Input type="number" min="0" step="0.01" value={it.unit_price} onChange={e => setOrderItems(p => p.map((r, i) => i===idx ? {...r, unit_price: parseFloat(e.target.value)||0} : r))} className="h-8 text-sm text-right" />
                          </td>
                          <td className="px-1 py-1.5">
                            <select value={it.vat_rate} onChange={e => setOrderItems(p => p.map((r, i) => i===idx ? {...r, vat_rate: parseFloat(e.target.value)} : r))} className="h-8 text-sm w-full border border-input rounded-md px-2 bg-background">
                              {VAT_RATES.map(r => <option key={r} value={r}>{r}%</option>)}
                            </select>
                          </td>
                          <td className="px-1 py-1.5">
                            <Button type="button" variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => setOrderItems(p => p.filter((_, i) => i !== idx))}>
                              <X size={12} />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              {/* Quick total */}
              {orderItems.some(it => it.unit_price > 0) && (
                <div className="flex justify-end text-sm font-semibold text-primary">
                  Totale: {formatEuro(
                    orderItems.reduce((s, it) => s + it.quantity * it.unit_price * (1 + it.vat_rate / 100), 0)
                  )}
                </div>
              )}
            </div>

            <div className="space-y-1.5">
              <Label>Note ordine</Label>
              <Textarea value={orderForm.notes} onChange={e => setOrderForm(f => ({ ...f, notes: e.target.value }))} rows={2} placeholder="Istruzioni speciali, condizioni..." />
            </div>
            <div className="flex gap-2 pt-1">
              <Button type="button" variant="outline" className="flex-1" onClick={() => setActiveDialog(null)}>Annulla</Button>
              <Button type="submit" className="flex-1 bg-sky-600 hover:bg-sky-700" disabled={saving} data-testid="save-cal-order-btn">
                {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Crea Ordine
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Activity quick-add form ──────────────────────────────────────────────── */}
      <Dialog open={activeDialog === 'activity'} onOpenChange={() => setActiveDialog(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }} className="flex items-center gap-2">
              <CheckSquare size={18} style={{ color: ACT_DEFAULT }} />Nuova Attività
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleActivityCreate} className="space-y-4 mt-2">
            <div className="space-y-1.5">
              <Label>Titolo *</Label>
              <Input value={actForm.title} onChange={e => setActForm(f => ({ ...f, title: e.target.value }))} required placeholder="Es. Chiamata follow-up cliente" data-testid="act-title-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Tipo</Label>
                <Select value={actForm.type} onValueChange={v => setActForm(f => ({ ...f, type: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {ACTIVITY_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Priorità</Label>
                <Select value={actForm.priority} onValueChange={v => setActForm(f => ({ ...f, priority: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Bassa</SelectItem>
                    <SelectItem value="medium">Media</SelectItem>
                    <SelectItem value="high">Alta</SelectItem>
                    <SelectItem value="urgent">Urgente</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Data scadenza *</Label>
              <Input type="date" value={actForm.due_date} onChange={e => setActForm(f => ({ ...f, due_date: e.target.value }))} required data-testid="act-due-date-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Note</Label>
              <Textarea value={actForm.description} onChange={e => setActForm(f => ({ ...f, description: e.target.value }))} rows={2} placeholder="Dettagli opzionali..." />
            </div>
            {users.length > 0 && (
              <div className="space-y-1.5">
                <Label>Assegna a</Label>
                <Select value={actForm.assigned_to || '__none__'} onValueChange={v => setActForm(f => ({ ...f, assigned_to: v === '__none__' ? '' : v }))}>
                  <SelectTrigger data-testid="act-assigned-to"><SelectValue placeholder="Seleziona commerciale..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">— Nessuno</SelectItem>
                    {users.map(u => <SelectItem key={u.id||u._id} value={u.id||u._id}>{u.name||u.email}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="flex gap-2 pt-1">
              <Button type="button" variant="outline" className="flex-1" onClick={() => setActiveDialog(null)}>Annulla</Button>
              <Button type="submit" className="flex-1 text-white" style={{ background: ACT_DEFAULT }} disabled={saving} data-testid="save-activity-btn">
                {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Crea Attività
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* ── Item detail popup (click on existing item) ───────────────────────── */}
      <ItemDetailDialog
        item={selectedItem}
        onClose={() => setSelectedItem(null)}
        navigate={navigate}
      />
    </div>
  );
}
